import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Medal } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANK_STYLES = [
  'bg-[#e8173a] text-white',
  'bg-white/15 text-white',
  'bg-white/10 text-[#c9c9c9]',
];

export default function WinnersBlock({ tournamentId, celebrate = true }) {
  const [winners, setWinners] = useState(null);
  const [seen, setSeen] = useState(false);
  const ref = useRef(null);
  const celebrated = useRef(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/tournaments/${tournamentId}/results`)
      .then((r) => {
        if (alive) setWinners(r.data.winners || []);
      })
      .catch(() => {
        if (alive) setWinners([]);
      });
    return () => {
      alive = false;
    };
  }, [tournamentId]);

  useEffect(() => {
    if (!winners || winners.length === 0 || seen || !ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin: '-40px' }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [winners, seen]);

  useEffect(() => {
    if (!celebrate || !seen || !winners || winners.length === 0 || celebrated.current) return;
    celebrated.current = true;
    const rect = ref.current?.getBoundingClientRect();
    const origin = rect
      ? { x: (rect.left + rect.width / 2) / window.innerWidth, y: Math.max(0.1, (rect.top + 40) / window.innerHeight) }
      : { y: 0.7 };
    const colors = ['#e8173a', '#ffffff', '#ffd700'];
    confetti({ particleCount: 90, spread: 80, startVelocity: 34, origin, colors, zIndex: 200, scalar: 0.9, disableForReducedMotion: true });
    setTimeout(() => {
      confetti({ particleCount: 45, spread: 110, startVelocity: 24, origin, colors, zIndex: 200, scalar: 0.7, disableForReducedMotion: true });
    }, 350);
  }, [seen, winners]);

  if (!winners || winners.length === 0) return null;

  return (
    <div ref={ref} className="relative border-t border-white/5 pt-4 overflow-hidden" data-testid={`winners-${tournamentId}`}>
      <motion.div
        initial={{ x: '-120%' }}
        animate={seen ? { x: '220%' } : {}}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-[#e8173a]/30 to-transparent pointer-events-none"
        aria-hidden="true"
      />
      <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#8a8a8a] flex items-center gap-2 mb-3">
        <Medal className="w-3.5 h-3.5 text-[#e8173a]" />
        Prize Pool Winners
      </p>
      <div className="space-y-2">
        {winners.slice(0, 5).map((w, i) => (
          <motion.div
            key={`${w.rank}-${w.team}`}
            initial={{ opacity: 0, x: -18 }}
            animate={seen ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3"
            data-testid={`winner-row-${tournamentId}-${w.rank}`}
          >
            <span
              className={`w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-black ${
                RANK_STYLES[w.rank - 1] || 'bg-white/5 text-[#8a8a8a]'
              }`}
            >
              {w.rank}
            </span>
            <span className="text-sm text-white font-semibold truncate flex-1">{w.team}</span>
            {w.prize > 0 && <span className="text-sm font-bold text-[#00c853]">₹{Number(w.prize).toLocaleString('en-IN')}</span>}
            {w.points > 0 && <span className="text-xs text-[#8a8a8a]">{w.points} pts</span>}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
